import { ORDER_SIDE, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { ORDER_TYPE } from 'constants/enums';
import { dayjs } from 'helpers/day';
import { multiply } from 'helpers/ramda';
import { scientificToDecimalNotation, cropAfterDecimals } from 'helpers/utils';
import { Position } from 'types/basketAnalyze';
import { OrdersObject } from 'types/basketOrders';

const basketAnalyzeGenerateApiPayload = ({
  asset,
  orders,
  defaultTargetPrice,
  defaultTargetDate,
  userRequestedPayload,
}: {
  asset: string;
  orders: OrdersObject[];
  defaultTargetPrice: number;
  defaultTargetDate: number;
  userRequestedPayload: {
    targetPrice?: number;
    targetDate?: number;
    selectedOrders?: OrdersObject[];
  };
}) => {
  const underlyingIndexSymbol =
    asset === 'BTC'
      ? `.DEXBT${VANILLA_SETTLING_ASSET}`
      : `.DE${asset}${VANILLA_SETTLING_ASSET}`;

  const targetPrice = userRequestedPayload?.targetPrice ?? defaultTargetPrice;

  const targetDate = userRequestedPayload?.targetDate
    ? Math.floor(userRequestedPayload.targetDate / 1000)
    : Math.floor(defaultTargetDate / 1000);

  const Orders = userRequestedPayload?.selectedOrders?.length
    ? userRequestedPayload?.selectedOrders
    : orders;

  const positions = Orders.map(order => {
    const isMarketOrderType = order.order_type === ORDER_TYPE.MARKET;
    const entryPrice = isMarketOrderType
      ? Number(order.mark_price)
      : Number(order.limit_price);

    const size = order?.size ?? 0;
    const formmattedSize = order.side === ORDER_SIDE.SELL ? size * -1 : size;

    const data: Position = {
      product_id: order.product_id,
      entry_price: entryPrice,
      size: formmattedSize,
    };
    return data;
  });

  const apiPayload = {
    underlying_index_symbol: underlyingIndexSymbol,
    target_date: targetDate,
    target_price: targetPrice,
    positions,
  };

  return apiPayload;
};

const basketAnalyzeFormatGreekValue = ({ greekValue, precision }) => {
  return Number(cropAfterDecimals(scientificToDecimalNotation(greekValue), precision));
};

const basketAnalyzeGetCheckDateUTC = (): Date => {
  const utcNow = dayjs().utc();
  const noonUTC = utcNow.startOf('day').set('hour', 12).set('minute', 0).set('second', 0); // 12:00 PM UTC

  // Return the current day if it's before 12:00 PM UTC, otherwise return the next day
  const checkDate = utcNow.isAfter(noonUTC)
    ? utcNow.add(1, 'day').startOf('day')
    : utcNow.startOf('day');

  return checkDate.toDate();
};

const basketAnalyzeComputeGreek = ({
  size,
  selector,
  productId,
  precision,
  contractType,
  side,
  greekType,
}) => {
  const rawGreekValue = Number(selector(productId) ?? 0);

  // adjustment sign factor mapping
  const adjustmentSignFactor =
    {
      call_options: {
        buy: { DELTA: 1, GAMMA: 1, THETA: -1, VEGA: 1 },
        sell: { DELTA: -1, GAMMA: -1, THETA: 1, VEGA: -1 },
      },
      put_options: {
        buy: { DELTA: -1, GAMMA: 1, THETA: -1, VEGA: 1 },
        sell: { DELTA: 1, GAMMA: -1, THETA: 1, VEGA: -1 },
      },
    }[contractType]?.[side]?.[greekType] ?? 1;

  const adjustedSignBasedRawGreekValue = Math.abs(rawGreekValue) * adjustmentSignFactor;

  return basketAnalyzeFormatGreekValue({
    greekValue: multiply(size, adjustedSignBasedRawGreekValue),
    precision,
  });
};

export {
  basketAnalyzeGenerateApiPayload,
  basketAnalyzeFormatGreekValue,
  basketAnalyzeGetCheckDateUTC,
  basketAnalyzeComputeGreek,
};
